import { wrapCreateBrowserRouter } from "@sentry/react/react-router"
import { NuqsAdapter } from "nuqs/adapters/react-router/v8"
import {
  LoaderFunctionArgs,
  Outlet,
  ScrollRestoration,
  createBrowserRouter,
  redirect,
  replace,
} from "react-router"
import { RouterProvider } from "react-router/dom"

import { url } from "@/lib/url"
import { XLIndex } from "@/src/components/admin/XL"

import { ErrorState } from "../components"
import { DocsPreview } from "../components/DocsPreview"
import { PageViewLogger } from "../components/PageViewLogger"
import { requireAdmin } from "./utils"

const Root = () => {
  return (
    <>
      <ScrollRestoration />
      <NuqsAdapter>
        <Outlet />
      </NuqsAdapter>
      <PageViewLogger />
      <DocsPreview />
    </>
  )
}

const sentryCreateBrowserRouter = wrapCreateBrowserRouter(createBrowserRouter)

export const router = sentryCreateBrowserRouter([
  {
    id: "root",
    element: <Root />,
    errorElement: (
      <ErrorState
        title="Something went wrong"
        subTitle="Try again or contact an administrator"
        retryLabel="Try again"
        className="h-screen w-screen rounded-none"
        onRetry={() => window.location.reload()}
      />
    ),
    children: [
      {
        id: "public-app",
        children: [
          {
            path: "/",
            element: <></>,
            // Decide from the current user here rather than always redirecting
            // to /admin, so non-admins go straight to their workspace (or
            // login) and never briefly land on the admin-gated /admin URL.
            loader: async () => {
              await requireAdmin()
              return redirect(url(["/admin"]))
            },
          },
          {
            id: "not-found",
            path: "/404",
            element: <div>404</div>,
          },
          {
            id: "login",
            path: "/login",
            lazy: () => import("./pages/login"),
          },
          {
            id: "auth-magic",
            path: "/auth/magic",
            lazy: () => import("./pages/auth/magic"),
          },
          {
            id: "auth-invitation",
            path: "/auth/invitation",
            lazy: () => import("./pages/auth/invitation"),
          },
          {
            path: "/greystar-ev",
            loader: () => redirect("/platform"),
          },
          {
            id: "org-welcome",
            path: "/:orgSlug/welcome",
            lazy: () => import("./pages/org/welcome"),
          },
          // Developer routes
          {
            id: "developers",
            lazy: () => import("./pages/developer-layout"),
            children: [
              {
                path: "/:orgSlug/invest",
                loader: ({ params }: LoaderFunctionArgs) => {
                  return replace(
                    url([
                      "/[orgSlug]/invest/origination",
                      { orgSlug: params["orgSlug"] ?? "" },
                    ]),
                  )
                },
              },
              {
                id: "invest-origination",
                path: "/:orgSlug/invest/origination",
                lazy: () => import("./pages/org/invest/origination"),
              },
              {
                path: "/:orgSlug/invest/origination/:bundlePid",
                lazy: () => import("./pages/org/invest/package-detail"),
                children: [
                  {
                    id: "invest-origination-create-bid",
                    path: "/:orgSlug/invest/origination/:bundlePid/project/:projectPid/create-bid",
                    lazy: () => import("./pages/org/invest/new-bid"),
                  },
                  {
                    id: "invest-origination-edit-bid",
                    path: "/:orgSlug/invest/origination/:bundlePid/project/:projectPid/bid/:bidPid",
                    lazy: () => import("./pages/org/invest/edit-bid"),
                  },
                  // Legacy site-keyed bid routes. A site can hold several
                  // projects in a package, so these could only ever reach one
                  // of them; send them back to the package to pick a project.
                  {
                    path: "/:orgSlug/invest/origination/:bundlePid/property/:sitePid/create-bid",
                    loader: ({ params }: LoaderFunctionArgs) =>
                      redirect(
                        url([
                          "/[orgSlug]/invest/origination/[bundlePid]",
                          {
                            orgSlug: params["orgSlug"] ?? "",
                            bundlePid: params["bundlePid"] ?? "",
                          },
                        ]),
                      ),
                  },
                  {
                    path: "/:orgSlug/invest/origination/:bundlePid/property/:sitePid/bid/:bidPid",
                    loader: ({ params }: LoaderFunctionArgs) =>
                      redirect(
                        url([
                          "/[orgSlug]/invest/origination/[bundlePid]",
                          {
                            orgSlug: params["orgSlug"] ?? "",
                            bundlePid: params["bundlePid"] ?? "",
                          },
                        ]),
                      ),
                  },
                ],
              },
              {
                id: "invest-origination-reports",
                path: "/:orgSlug/invest/origination/:bundlePid/reports",
                lazy: () => import("./pages/org/invest/package-reports"),
              },

              {
                id: "invest-development",
                path: "/:orgSlug/invest/development",
                lazy: () => import("./pages/org/invest/development"),
              },

              {
                id: "invest-meeting-notes",
                path: "/:orgSlug/invest/meeting-notes",
                loader: () => requireAdmin(),
                lazy: () => import("./pages/org/invest/meeting-notes"),
              },
              {
                id: "invest-meeting-note-detail",
                path: "/:orgSlug/invest/meeting-notes/:pid",
                loader: () => requireAdmin(),
                lazy: () => import("./pages/org/invest/meeting-note-detail"),
              },
              {
                id: "invest-meeting-note-extractions",
                path: "/:orgSlug/invest/meeting-notes/:pid/extractions",
                loader: () => requireAdmin(),
                lazy: () => import("./pages/org/invest/meeting-note-extractions"),
              },
              {
                id: "invest-meeting-note-extraction-detail",
                path: "/:orgSlug/invest/meeting-notes/:pid/extractions/:extractionPid",
                loader: () => requireAdmin(),
                lazy: () => import("./pages/org/invest/meeting-note-extraction-detail"),
              },

              {
                id: "invest-settings",
                path: "/:orgSlug/invest/settings",
                lazy: () => import("./pages/org/settings"),
              },
              {
                path: "/:orgSlug/invest/:bundlePid",
                loader: ({ params }: LoaderFunctionArgs) => {
                  return redirect(
                    url([
                      "/[orgSlug]/invest/origination/[bundlePid]",
                      {
                        orgSlug: params["orgSlug"] ?? "",
                        bundlePid: params["bundlePid"] ?? "",
                      },
                    ]),
                  )
                },
              },

              {
                path: "/:orgSlug/projects",
                loader: ({ params }: LoaderFunctionArgs) => {
                  return redirect(
                    url([
                      "/[orgSlug]/invest/development",
                      { orgSlug: params["orgSlug"] ?? "" },
                    ]),
                  )
                },
              },
              // Investor property pages (new project-based URLs)
              {
                id: "investor-project-detail-layout",
                lazy: () => import("./pages/org/invest/property-layout"),
                children: [
                  {
                    id: "invest-project-summary",
                    path: "/:orgSlug/invest/development/project/:projectPid",
                    lazy: () => import("./pages/org/invest/property-summary"),
                  },
                  {
                    id: "invest-project-documents",
                    path: "/:orgSlug/invest/development/project/:projectPid/documents",
                    lazy: () => import("./pages/org/invest/property-documents"),
                  },
                  {
                    id: "invest-project-bids",
                    path: "/:orgSlug/invest/development/project/:projectPid/bids",
                    lazy: () => import("./pages/org/invest/property-bids"),
                  },
                  {
                    id: "invest-project-milestones",
                    path: "/:orgSlug/invest/development/project/:projectPid/milestones",
                    lazy: () => import("./pages/org/invest/property-milestones"),
                  },
                ],
              },
              // Legacy property page redirects (bundlePid/property/sitePid → development table)
              {
                path: "/:orgSlug/invest/development/:bundlePid/property/:sitePid",
                loader: ({ params }: LoaderFunctionArgs) => {
                  return redirect(
                    url([
                      "/[orgSlug]/invest/development",
                      { orgSlug: params["orgSlug"] ?? "" },
                    ]),
                  )
                },
              },
              {
                path: "/:orgSlug/invest/development/:bundlePid/property/:sitePid/documents",
                loader: ({ params }: LoaderFunctionArgs) => {
                  return redirect(
                    url([
                      "/[orgSlug]/invest/development",
                      { orgSlug: params["orgSlug"] ?? "" },
                    ]),
                  )
                },
              },
              {
                path: "/:orgSlug/invest/development/:bundlePid/property/:sitePid/bids",
                loader: ({ params }: LoaderFunctionArgs) => {
                  return redirect(
                    url([
                      "/[orgSlug]/invest/development",
                      { orgSlug: params["orgSlug"] ?? "" },
                    ]),
                  )
                },
              },
              {
                path: "/:orgSlug/invest/:bundlePid/property/:sitePid",
                loader: ({ params }: LoaderFunctionArgs) => {
                  return redirect(
                    url([
                      "/[orgSlug]/invest/development",
                      { orgSlug: params["orgSlug"] ?? "" },
                    ]),
                  )
                },
              },
              {
                path: "/:orgSlug/invest/:bundlePid/property/:sitePid/documents",
                loader: ({ params }: LoaderFunctionArgs) => {
                  return redirect(
                    url([
                      "/[orgSlug]/invest/development",
                      { orgSlug: params["orgSlug"] ?? "" },
                    ]),
                  )
                },
              },
              {
                path: "/:orgSlug/invest/:bundlePid/property/:sitePid/bids",
                loader: ({ params }: LoaderFunctionArgs) => {
                  return redirect(
                    url([
                      "/[orgSlug]/invest/development",
                      { orgSlug: params["orgSlug"] ?? "" },
                    ]),
                  )
                },
              },
            ],
          },
          // Building owner routes
          {
            id: "building-owner",
            lazy: () => import("./pages/building-owner-layout"),
            children: [
              {
                id: "org-index",
                path: "/:orgSlug",
                lazy: () => import("./pages/org/index"),
              },
              {
                id: "org-portfolio",
                path: "/:orgSlug/portfolio",
                lazy: () => import("./pages/org/portfolio"),
              },
              {
                id: "org-settings",
                path: "/:orgSlug/settings",
                lazy: () => import("./pages/org/settings"),
              },
              {
                id: "org-properties",
                path: "/:orgSlug/properties",
                lazy: () => import("./pages/org/properties/index"),
              },
              {
                id: "org-properties-add",
                path: "/:orgSlug/properties/add",
                lazy: () => import("./pages/org/properties/add/index"),
              },
              {
                id: "org-emails",
                path: "/:orgSlug/org-emails",
                lazy: () => import("./pages/org/org-emails"),
              },
              {
                id: "org-reonomy",
                path: "/:orgSlug/reonomy",
                lazy: () => import("./pages/org/properties/add/reonomy"),
              },
              {
                id: "org-uploads",
                path: "/:orgSlug/uploads",
                lazy: () => import("./pages/org/properties/add/uploads"),
              },
              {
                id: "org-upload-detail",
                path: "/:orgSlug/uploads/:csvImportPid",
                lazy: () => import("./pages/org/properties/add/upload"),
              },
              {
                id: "org-bundles",
                path: "/:orgSlug/bundles",
                lazy: () => import("./pages/org/properties/bundles/index"),
              },
              {
                id: "org-bundle-parents",
                path: "/:orgSlug/bundles/parents",
                lazy: () => import("./pages/org/properties/bundles/parents/index"),
              },
              {
                id: "org-bundle-parent-creation",
                path: "/:orgSlug/bundles/parents/create",
                lazy: () => import("./pages/org/properties/bundles/parents/create"),
              },
              {
                id: "org-bundle-parent-detail",
                path: "/:orgSlug/bundles/parents/:bundleParentPid",
                lazy: () => import("./pages/org/properties/bundles/parents/detail"),
              },
              {
                id: "org-bundle-parent-edit",
                path: "/:orgSlug/bundles/parents/:bundleParentPid/edit",
                lazy: () => import("./pages/org/properties/bundles/parents/edit"),
              },
              {
                id: "org-bundle-parent-award",
                path: "/:orgSlug/bundles/parents/:bundleParentPid/award",
                lazy: () => import("./pages/org/properties/bundles/parents/award"),
              },
              {
                id: "org-bundle-parent-award-notices",
                path: "/:orgSlug/bundles/parents/:bundleParentPid/award-notices",
                lazy: () =>
                  import("./pages/org/properties/bundles/parents/award-notices"),
              },

              {
                id: "org-energize",
                path: "/:orgSlug/energize",
                lazy: () => import("./pages/org/energize"),
              },
              // Marketplace
              {
                id: "org-marketplace",
                path: "/:orgSlug/marketplace",
                lazy: () => import("./pages/org/marketplace/index"),
              },
              {
                id: "org-marketplace-bids",
                path: "/:orgSlug/marketplace/bids",
                lazy: () => import("./pages/org/marketplace/bids"),
              },
              {
                id: "org-marketplace-properties",
                path: "/:orgSlug/marketplace/properties",
                lazy: () => import("./pages/org/marketplace/sites"),
              },
              {
                id: "org-marketplace-packages",
                path: "/:orgSlug/marketplace/packages",
                lazy: () => import("./pages/org/marketplace/packages/index"),
              },
              {
                id: "org-marketplace-package-detail",
                path: "/:orgSlug/marketplace/packages/:bundlePid",
                lazy: () => import("./pages/org/marketplace/packages/bundle-detail"),
              },
              {
                id: "org-marketplace-package-emails",
                path: "/:orgSlug/marketplace/packages/:bundlePid/emails",
                lazy: () => import("./pages/org/marketplace/packages/bundle-emails"),
              },
              {
                path: "/:orgSlug/marketplace/packages/:bundlePid/project/:projectPid",
                lazy: () => import("./pages/org/marketplace/packages/site/site-detail"),
                children: [
                  {
                    id: "org-marketplace-project-bid-detail",
                    path: "/:orgSlug/marketplace/packages/:bundlePid/project/:projectPid/bid/:bidPid",
                    lazy: () =>
                      import("./pages/org/marketplace/packages/site/bid-detail"),
                  },
                ],
              },
              // Legacy site-keyed package-property routes. A site can hold
              // several projects in a package, so these could only ever reach
              // one of them; send them back to the package to pick a project.
              {
                path: "/:orgSlug/marketplace/packages/:bundlePid/property/:sitePid",
                loader: ({ params }: LoaderFunctionArgs) =>
                  redirect(
                    url([
                      "/[orgSlug]/marketplace/packages/[bundlePid]",
                      {
                        orgSlug: params["orgSlug"] ?? "",
                        bundlePid: params["bundlePid"] ?? "",
                      },
                    ]),
                  ),
              },
              {
                path: "/:orgSlug/marketplace/packages/:bundlePid/property/:sitePid/bid/:bidPid",
                loader: ({ params }: LoaderFunctionArgs) =>
                  redirect(
                    url([
                      "/[orgSlug]/marketplace/packages/[bundlePid]",
                      {
                        orgSlug: params["orgSlug"] ?? "",
                        bundlePid: params["bundlePid"] ?? "",
                      },
                    ]),
                  ),
              },
              {
                id: "property-pages",
                path: "/:orgSlug/properties/:sitePid",
                lazy: () => import("./pages/org/properties/detail/layout"),
                children: [
                  {
                    id: "property-redirect",
                    path: "/:orgSlug/properties/:sitePid",
                    lazy: () => import("./pages/org/properties/detail/root"),
                  },
                  {
                    id: "property-documents",
                    path: "/:orgSlug/properties/:sitePid/documents",
                    lazy: () => import("./pages/org/properties/detail/documents"),
                  },
                  {
                    id: "property-general",
                    path: "/:orgSlug/properties/:sitePid/general",
                    lazy: () => import("./pages/org/properties/detail/general"),
                  },
                  {
                    id: "property-ev",
                    path: "/:orgSlug/properties/:sitePid/ev",
                    lazy: () => import("./pages/org/properties/detail/ev"),
                  },
                  {
                    path: "/:orgSlug/properties/:sitePid/marketplace",
                    lazy: () =>
                      import("./pages/org/properties/detail/marketplace/layout"),

                    children: [
                      {
                        id: "property-marketplace-preparing-bid-launch",
                        path: "/:orgSlug/properties/:sitePid/marketplace/preparing-bid-launch",
                        lazy: () =>
                          import("./pages/org/properties/detail/marketplace/preparing-bid-launch"),
                      },
                      {
                        id: "property-marketplace-collecting-bids",
                        path: "/:orgSlug/properties/:sitePid/marketplace/collecting-bids",
                        lazy: () =>
                          import("./pages/org/properties/detail/marketplace/collecting-bids"),
                      },
                      {
                        id: "property-marketplace-reviewing-bids",
                        path: "/:orgSlug/properties/:sitePid/marketplace/reviewing-bids",
                        lazy: () =>
                          import("./pages/org/properties/detail/marketplace/reviewing-bids"),
                      },
                      {
                        id: "property-marketplace-negotiating-contracts",
                        path: "/:orgSlug/properties/:sitePid/marketplace/negotiating-contracts",
                        lazy: () =>
                          import("./pages/org/properties/detail/marketplace/negotiating-contracts"),
                      },
                      {
                        id: "property-marketplace-developing-project",
                        path: "/:orgSlug/properties/:sitePid/marketplace/developing-project",
                        lazy: () =>
                          import("./pages/org/properties/detail/marketplace/developing-project"),
                      },
                      {
                        id: "property-marketplace-under-construction",
                        path: "/:orgSlug/properties/:sitePid/marketplace/under-construction",
                        lazy: () =>
                          import("./pages/org/properties/detail/marketplace/under-construction"),
                      },
                      {
                        id: "property-marketplace-energize",
                        path: "/:orgSlug/properties/:sitePid/marketplace/energize",
                        lazy: () =>
                          import("./pages/org/properties/detail/marketplace/energize"),
                      },

                      // Project specific
                      {
                        path: "/:orgSlug/properties/:sitePid/marketplace/:projectPid",

                        children: [
                          {
                            id: "property-marketplace-project-homepage",
                            path: "/:orgSlug/properties/:sitePid/marketplace/:projectPid",
                            lazy: () =>
                              import("./pages/org/properties/detail/marketplace/project-homepage"),
                          },
                          {
                            id: "property-marketplace-project-preparing-bid-launch",
                            path: "/:orgSlug/properties/:sitePid/marketplace/:projectPid/preparing-bid-launch",
                            lazy: () =>
                              import("./pages/org/properties/detail/marketplace/preparing-bid-launch"),
                          },
                          {
                            id: "property-marketplace-project-collecting-bids",
                            path: "/:orgSlug/properties/:sitePid/marketplace/:projectPid/collecting-bids",
                            lazy: () =>
                              import("./pages/org/properties/detail/marketplace/collecting-bids"),
                          },
                          {
                            id: "property-marketplace-project-reviewing-bids",
                            path: "/:orgSlug/properties/:sitePid/marketplace/:projectPid/reviewing-bids",
                            lazy: () =>
                              import("./pages/org/properties/detail/marketplace/reviewing-bids"),
                          },
                          {
                            id: "property-marketplace-project-negotiating-contracts",
                            path: "/:orgSlug/properties/:sitePid/marketplace/:projectPid/negotiating-contracts",
                            lazy: () =>
                              import("./pages/org/properties/detail/marketplace/negotiating-contracts"),
                          },
                          {
                            id: "property-marketplace-project-developing-project",
                            path: "/:orgSlug/properties/:sitePid/marketplace/:projectPid/developing-project",
                            lazy: () =>
                              import("./pages/org/properties/detail/marketplace/developing-project"),
                          },
                          {
                            id: "property-marketplace-project-under-construction",
                            path: "/:orgSlug/properties/:sitePid/marketplace/:projectPid/under-construction",
                            lazy: () =>
                              import("./pages/org/properties/detail/marketplace/under-construction"),
                          },
                          {
                            id: "property-marketplace-project-energize",
                            path: "/:orgSlug/properties/:sitePid/marketplace/:projectPid/energize",
                            lazy: () =>
                              import("./pages/org/properties/detail/marketplace/energize"),
                          },
                        ],
                      },
                    ],
                  },
                  {
                    path: "/:orgSlug/properties/:sitePid/solar",
                    lazy: () => import("./pages/org/properties/detail/evaluate-solar"),
                    children: [
                      {
                        id: "property-solar-bess",
                        path: "/:orgSlug/properties/:sitePid/solar/bess",
                        lazy: () =>
                          import("./pages/org/properties/detail/financing/bess"),
                      },
                      {
                        id: "property-solar-result",
                        path: "/:orgSlug/properties/:sitePid/solar/:resultPid",
                        lazy: () =>
                          import("./pages/org/properties/detail/financing/detail"),
                      },
                    ],
                  },
                  {
                    path: "/:orgSlug/properties/:sitePid/*",
                    loader: ({ params }: LoaderFunctionArgs) => {
                      return redirect(
                        url([
                          "/[orgSlug]/properties/[sitePid]",
                          {
                            orgSlug: params["orgSlug"] ?? "",
                            sitePid: params["sitePid"] ?? "",
                          },
                        ]),
                      )
                    },
                  },
                  {
                    id: "property-electricity",
                    path: "/:orgSlug/properties/:sitePid/electricity",
                    lazy: () => import("./pages/org/properties/detail/electricity"),
                  },

                  {
                    id: "property-custom-tariff",
                    path: "/:orgSlug/properties/:sitePid/electricity/tariffs/custom/:tariffId",
                    lazy: () => import("./pages/org/properties/detail/custom_tariff"),
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        id: "admin",
        // Guard the whole /admin subtree at the loader level: requireAdmin
        // redirects non-admins to their workspace (or login) and returns the
        // current user, which the landing reads via useRouteLoaderData("admin").
        loader: () => requireAdmin(),
        children: [
          {
            id: "admin-layout",
            lazy: () => import("./pages/admin/layout"),
            children: [
              {
                id: "admin-index",
                path: "/admin",
                lazy: () => import("./pages/admin/landing"),
                handle: { title: "Home" },
              },
              {
                id: "admin-queue",
                path: "/admin/queue",
                lazy: () => import("./pages/admin/queue"),
                handle: { title: "Queue" },
              },
              {
                id: "admin-job-activity",
                path: "/admin/job-activity",
                lazy: () => import("./pages/admin/job-activity"),
                handle: { title: "Lux Job Activity" },
              },
              {
                id: "admin-solar-detection",
                path: "/admin/solar-detection",
                lazy: () => import("./pages/admin/solar-detection"),
                handle: { title: "Solar detection" },
              },
              {
                id: "admin-projects",
                path: "/admin/projects",
                lazy: () => import("./pages/admin/projects"),
                handle: { title: "Marketplace" },
              },
              {
                id: "admin-documents",
                path: "/admin/documents",
                lazy: () => import("./pages/admin/documents"),
                handle: { title: "Documents" },
              },
              {
                id: "admin-document-viewer",
                path: "/admin/documents/:documentPid",
                lazy: () => import("./pages/admin/document-viewer"),
                handle: { title: "Document Viewer" },
              },
              {
                id: "admin-lease-analysis",
                path: "/admin/lease-analysis",
                lazy: () => import("./pages/admin/lease-analysis"),
                handle: { title: "Lease Analysis" },
              },
              {
                id: "admin-bulk-update",
                path: "/admin/bulk-update",
                lazy: () => import("./pages/admin/bulk-update"),
                handle: { title: "Bulk Update" },
              },
              {
                id: "admin-document-reprocess",
                path: "/admin/document-reprocess",
                lazy: () => import("./pages/admin/document-reprocess"),
                handle: { title: "Reprocess Document Analysis" },
              },
              {
                id: "admin-multiportfolio",
                path: "/admin/multiportfolio",
                lazy: () => import("./pages/admin/multiportfolio"),
                handle: { title: "Multi Portfolio" },
              },
              {
                id: "admin-impact",
                path: "/admin/impact",
                lazy: () => import("./pages/admin/impact"),
                handle: { title: "Lumen Impact" },
              },
              {
                id: "admin-finmodel",
                path: "/admin/finmodel",
                lazy: () => import("./pages/admin/finmodel/versions"),
                handle: { title: "Finmodel" },
              },
              {
                id: "admin-finmodel-versions",
                path: "/admin/finmodel/versions",
                loader: () => redirect("/admin/finmodel"),
              },
              {
                id: "admin-finmodel-failures",
                path: "/admin/finmodel/failures",
                loader: () => redirect("/admin/finmodel?tab=failures"),
              },
              {
                id: "admin-finmodel-failure-detail",
                path: "/admin/finmodel/failures/:failureId",
                lazy: () => import("./pages/admin/finmodel/failures/detail"),
                handle: { title: "Finmodel failure" },
              },
              {
                id: "admin-finmodel-failures-facet",
                path: "/admin/finmodel/failures/facet",
                lazy: () => import("./pages/admin/finmodel/failures/facet"),
                handle: { title: "Finmodel failures" },
              },
              {
                id: "admin-slack-channels",
                path: "/admin/slack-channels",
                lazy: () => import("./pages/admin/slack-channels"),
                handle: { title: "Slack Channels" },
              },
              {
                id: "admin-solar-captcha",
                path: "/admin/solar-captcha",
                lazy: () => import("./pages/admin/solar-captcha"),
                handle: { title: "Solar captcha" },
              },
              {
                id: "admin-api-keys",
                path: "/admin/api-keys",
                lazy: () => import("./pages/admin/api-keys"),
                handle: { title: "API Keys" },
              },
              {
                id: "admin-batteries",
                path: "/admin/batteries",
                lazy: () => import("./pages/admin/batteries"),
                handle: { title: "Batteries" },
              },
              {
                id: "admin-workstreams",
                path: "/admin/workstreams",
                lazy: () => import("./pages/admin/workstreams"),
                handle: { title: "Workstreams" },
              },
              {
                id: "admin-ftm-programs",
                path: "/admin/ftm-programs",
                lazy: () => import("./pages/admin/ftm-programs"),
                handle: { title: "FTM Programs" },
              },
              {
                id: "admin-ftm-pricing",
                path: "/admin/ftm-pricing",
                lazy: () => import("./pages/admin/ftm-pricing"),
                handle: { title: "FTM Pricing" },
              },
              {
                id: "admin-users",
                path: "/admin/users",
                lazy: () => import("./pages/admin/users/index"),
                handle: { title: "Users" },
              },
              {
                id: "admin-user-detail",
                path: "/admin/users/:userId",
                lazy: () => import("./pages/admin/users/detail"),
                handle: { title: "User" },
              },
              {
                id: "admin-users-new",
                path: "/admin/users/new",
                lazy: () => import("./pages/admin/users/new"),
                handle: { title: "Add Users" },
              },
              {
                id: "admin-tariff-detail",
                path: "/admin/tariffs/:tariffId",
                lazy: () => import("./pages/admin/tariffs/detail"),
                handle: { title: "Tariff" },
              },
              {
                id: "admin-tariff-revision-detail",
                path: "/admin/tariffs/:tariffId/revisions/:revisionId",
                lazy: () => import("./pages/admin/tariffs/revision"),
                handle: { title: "Tariff Revision" },
              },
              {
                id: "admin-tariff-revision-draft",
                path: "/admin/tariffs/:tariffId/revisions/draft/:draftPid",
                lazy: () => import("./pages/admin/tariffs/draft"),
                handle: { title: "Tariff Draft" },
              },
              {
                id: "admin-policies",
                path: "/admin/policies",
                lazy: () => import("./pages/admin/policies/index"),
              },
              {
                id: "admin-policy-detail",
                path: "/admin/policies/:policyPid",
                lazy: () => import("./pages/admin/policies/detail"),
                handle: { title: "Policy Detail" },
              },
              {
                id: "admin-utilities",
                path: "/admin/utilities",
                lazy: () => import("./pages/admin/utilities/index"),
                handle: { title: "Utilities" },
              },
              {
                id: "admin-utility-detail",
                path: "/admin/utilities/:utilitySlug",
                lazy: () => import("./pages/admin/utilities/detail"),
              },
              {
                id: "admin-utility-tariffs-redirect",
                path: "/admin/utilities/:utilitySlug/tariffs",
                loader: ({ params }: LoaderFunctionArgs) =>
                  redirect(
                    url([
                      "/admin/utilities/[slug]",
                      { slug: params["utilitySlug"] ?? "" },
                    ]),
                  ),
              },
              {
                id: "admin-urdb",
                path: "/admin/urdb",
                lazy: () => import("./pages/admin/urdb/index"),
                handle: { title: "URDB Utilities" },
              },
              {
                id: "admin-urdb-utility",
                path: "/admin/urdb/utilities/:eiaId",
                lazy: () => import("./pages/admin/urdb/utility"),
                handle: { title: "URDB Utility" },
              },
              {
                id: "admin-genability",
                path: "/admin/genability",
                lazy: () => import("./pages/admin/genability/index"),
                handle: { title: "Genability" },
              },
              {
                id: "admin-genability-utility",
                path: "/admin/genability/utilities/:lseId",
                lazy: () => import("./pages/admin/genability/utility"),
                handle: { title: "Genability Utility" },
              },
              {
                id: "admin-genability-tariff",
                path: "/admin/genability/tariffs/:masterTariffId",
                lazy: () => import("./pages/admin/genability/tariff"),
                handle: { title: "Genability Tariff" },
              },
              {
                id: "admin-genability-revision",
                path: "/admin/genability/revisions/:revisionId",
                lazy: () => import("./pages/admin/genability/revision"),
                handle: { title: "Genability Revision" },
              },
              {
                id: "admin-urdb-rate",
                path: "/admin/urdb/rates/:urdbId",
                lazy: () => import("./pages/admin/urdb/rate"),
                handle: { title: "URDB Rate" },
              },
              {
                id: "admin-orgs",
                path: "/admin/orgs",
                lazy: () => import("./pages/admin/org/index"),
                handle: { title: "Organizations" },
              },
              {
                id: "admin-developer-contracts",
                path: "/admin/developer-contracts",
                lazy: () => import("./pages/admin/developer-contracts/index"),
                handle: { title: "Developer Contracts" },
              },
              {
                id: "admin-developer-contract-detail",
                path: "/admin/developer-contracts/:orgPid",
                lazy: () => import("./pages/admin/developer-contracts/detail"),
                handle: { title: "Developer Contract Detail" },
              },
              {
                id: "admin-org-detail",
                path: "/admin/orgs/:orgPid",
                lazy: () => import("./pages/admin/org/detail/index"),
                handle: { title: "Org Detail" },
              },
              {
                id: "admin-org-physical-check",
                path: "/admin/orgs/:orgPid/physical-check",
                lazy: () => import("./pages/admin/org/detail/physical-check"),
              },
              {
                id: "admin-org-products",
                path: "/admin/orgs/:orgPid/products",
                lazy: () => import("./pages/admin/org/detail/finproducts"),
                handle: { title: "FinProducts" },
              },
              {
                id: "admin-org-product-detail",
                path: "/admin/orgs/:orgPid/products/:productId",
                lazy: () => import("./pages/admin/org/detail/finproduct-detail"),
                handle: { title: "Org Detail" },
              },
              {
                id: "admin-portfolios",
                path: "/admin/portfolios",
                lazy: () => import("./pages/admin/portfolios/index"),
                handle: { title: "Portfolios - Admin" },
              },
              {
                id: "admin-portfolio-detail",
                path: "/admin/portfolios/:portfolioPid",
                lazy: () => import("./pages/admin/portfolios/detail"),
              },
              {
                id: "admin-portfolio-bulk",
                path: "/admin/portfolios/:portfolioPid/bulk",
                lazy: () => import("./pages/admin/portfolios/bulk"),
              },
              {
                id: "admin-portfolio-energy",
                path: "/admin/portfolios/:portfolioPid/energy",
                lazy: () => import("./pages/admin/portfolios/energy"),
              },
              {
                id: "admin-companies",
                path: "/admin/companies",
                lazy: () => import("./pages/admin/companies"),
              },
              {
                id: "admin-company-duplicates",
                path: "/admin/companies/duplicates",
                lazy: () => import("./pages/admin/companies/duplicates"),
                handle: { title: "Company duplicates" },
              },
              {
                id: "admin-company-detail",
                path: "/admin/companies/:companyPid",
                lazy: () => import("./pages/admin/companies/detail"),
              },
              {
                id: "admin-ownership-review",
                path: "/admin/ownership-review",
                lazy: () => import("./pages/admin/ownership-review"),
              },
              {
                id: "admin-interconnection-segments",
                path: "/admin/interconnection-segments",
                handle: { title: "Interconnection" },
                lazy: () => import("./pages/admin/interconnection-segments"),
              },
              {
                id: "admin-interconnection-segment-detail",
                path: "/admin/interconnection-segments/:pk",
                handle: { title: "Interconnection Segment" },
                lazy: () => import("./pages/admin/interconnection-segments/detail"),
              },
              {
                id: "admin-substation-detail",
                path: "/admin/substations/:utilityId/:substation",
                handle: { title: "Substation" },
                lazy: () => import("./pages/admin/substations/detail"),
              },
              {
                id: "admin-buildings",
                path: "/admin/buildings",
                lazy: () => import("./pages/admin/buildings"),
              },
              {
                id: "admin-building-detail",
                path: "/admin/buildings/:buildingPid",
                lazy: () => import("./pages/admin/buildings/detail"),
              },
              {
                id: "admin-add-buildings",
                path: "/admin/buildings/add",
                lazy: () => import("./pages/admin/buildings/add/select"),
              },
              {
                id: "admin-add-single-building",
                path: "/admin/buildings/add/single",
                lazy: () => import("./pages/admin/buildings/add/single"),
              },
              {
                id: "admin-upload-buildings",
                path: "/admin/buildings/add/upload",
                lazy: () => import("./pages/admin/buildings/add/upload"),
              },
              {
                id: "admin-add-multi-buildings",
                path: "/admin/buildings/add/multi/:csvImportPid",
                lazy: () => import("./pages/admin/buildings/add/multi"),
              },
              {
                id: "admin-sites",
                path: "/admin/sites",
                lazy: () => import("./pages/admin/index"),
              },
              {
                id: "admin-site-layout",
                lazy: () => import("./pages/admin/sites/layout"),
                children: [
                  {
                    id: "admin-site-index",
                    path: "/admin/sites/:sitePid",
                    lazy: () => import("./pages/admin/sites/index"),
                  },
                  {
                    id: "admin-site-policy",
                    path: "/admin/sites/:sitePid/policy",
                    lazy: () => import("./pages/admin/sites/policy"),
                  },
                  {
                    id: "admin-site-helioscopes",
                    path: "/admin/sites/:sitePid/helioscopes",
                    lazy: () => import("./pages/admin/sites/helioscopes"),
                  },
                  {
                    id: "admin-site-changelog",
                    path: "/admin/sites/:sitePid/changelog",
                    lazy: () => import("./pages/admin/sites/changelog"),
                  },
                  {
                    id: "admin-site-building-data",
                    path: "/admin/sites/:sitePid/building-data",
                    lazy: () => import("./pages/admin/sites/building-data"),
                  },
                  {
                    id: "admin-site-status",
                    path: "/admin/sites/:sitePid/status",
                    lazy: () => import("./pages/admin/sites/status"),
                  },
                  {
                    id: "admin-site-scenarios",
                    path: "/admin/sites/:sitePid/scenarios",
                    lazy: () => import("./pages/admin/sites/scenarios/index"),
                  },
                  {
                    id: "admin-site-scenario-detail",
                    path: "/admin/sites/:sitePid/scenarios/:scenarioPid",
                    lazy: () => import("./pages/admin/sites/scenarios/detail"),
                  },
                  {
                    id: "admin-site-results",
                    path: "/admin/sites/:sitePid/results",
                    lazy: () => import("./pages/admin/sites/results/index"),
                  },
                  {
                    id: "admin-site-result-detail",
                    path: "/admin/sites/:sitePid/results/:resultId",
                    lazy: () => import("./pages/admin/sites/results/detail"),
                  },
                  {
                    id: "admin-site-lux",
                    path: "/admin/sites/:sitePid/lux/:luxId",
                    lazy: () => import("./pages/admin/sites/lux"),
                  },
                  {
                    id: "admin-site-finmodels",
                    path: "/admin/sites/:sitePid/finmodels",
                    lazy: () => import("./pages/admin/sites/finmodels/index"),
                  },
                  {
                    id: "admin-site-finmodel-detail",
                    path: "/admin/sites/:sitePid/finmodels/:finmodelId",
                    lazy: () => import("./pages/admin/sites/finmodels/detail"),
                  },
                  {
                    id: "admin-site-finmodel-compare",
                    path: "/admin/sites/:sitePid/finmodels/:finmodelId/compare",
                    lazy: () => import("./pages/admin/sites/finmodels/compare"),
                  },
                  {
                    id: "admin-site-projects",
                    path: "/admin/sites/:sitePid/projects",
                    lazy: () => import("./pages/admin/sites/projects/index"),
                  },
                  {
                    id: "admin-site-etb",
                    path: "/admin/sites/:sitePid/etb",
                    lazy: () => import("./pages/admin/sites/etb/index"),
                  },
                  {
                    id: "admin-site-etb-detail",
                    path: "/admin/sites/:sitePid/etb/:etbId",
                    lazy: () => import("./pages/admin/sites/etb/detail"),
                  },
                  {
                    id: "admin-site-energy",
                    path: "/admin/sites/:sitePid/energy",
                    lazy: () => import("./pages/admin/sites/energy/index"),
                  },
                  {
                    id: "admin-site-energy-multi-meter",
                    path: "/admin/sites/:sitePid/energy/multi-meter",
                    lazy: () => import("./pages/admin/sites/energy/multi-meter/index"),
                  },
                  {
                    id: "admin-site-energy-meter-set",
                    path: "/admin/sites/:sitePid/energy/multi-meter/set/:meterSetId",
                    lazy: () => import("./pages/admin/sites/energy/multi-meter/set"),
                  },
                  {
                    id: "admin-site-energy-meter",
                    path: "/admin/sites/:sitePid/energy/multi-meter/:meterId",
                    lazy: () => import("./pages/admin/sites/energy/multi-meter/meter"),
                  },
                  {
                    id: "admin-site-energy-name-that-tariff",
                    path: "/admin/sites/:sitePid/energy/multi-meter/:meterId/name-that-tariff",
                    lazy: () =>
                      import("./pages/admin/sites/energy/multi-meter/name-that-tariff"),
                  },
                  {
                    id: "admin-site-energy-billing-meters",
                    path: "/admin/sites/:sitePid/energy/billing-meters",
                    lazy: () => import("./pages/admin/sites/energy/billing-meters"),
                  },
                  {
                    id: "admin-site-energy-espm",
                    path: "/admin/sites/:sitePid/energy/espm",
                    lazy: () => import("./pages/admin/sites/energy/espm"),
                  },
                ],
              },
            ],
          },
          {
            id: "admin-xlsx",
            path: "/admin/xlsx",
            element: <XLIndex />,
          },
        ],
      },
    ],
  },
])

export function ReactRouter() {
  return <RouterProvider router={router} />
}
